import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Modal, ModalHeader, ModalBody} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {ContentGapsExplanation} from './ContentGapsBadge';
import {documentGaps} from './docConstants';
import {locateQuote, splitByRanges, textIsUngroundable} from './quoteLocator';
import './DocumentRawModal.scss';

/**
 * Visor read-only del crudo de un documento (agency-only). Trae el `raw_text` on-demand al abrir
 * (el listado es liviano). El crudo NUNCA se comparte con el cliente — sólo se muestra acá.
 *
 * Con `quote` (la cita textual de un hecho, `fact.source_quote`) el visor además **lleva hasta la
 * cita**: la resalta en el texto, hace scroll hasta ahí al abrir, y si aparece más de una vez deja
 * recorrerlas. Es la trazabilidad que Pablo pidió el 10/09 («que me mande al documento y a dónde
 * está»): antes el camino era memorizar el nombre del documento, abrir la biblioteca, encontrarlo
 * a mano y hacer Ctrl+F con una frase que ya no se recordaba.
 *
 * La cita se busca con la MISMA regla con la que el backend la dio por sustentada
 * (`quoteLocator`, espejo de `fact_grounding`): si el gate la encontró, el visor la encuentra. Y
 * cuando NO está —material hablado, donde el gate no la verifica, o una cita parafraseada— se
 * dice, en vez de abrir el texto arriba de todo y dejar que el usuario concluya que el visor no
 * anda.
 */
export function DocumentRawModal({isOpen, document, clientId, toggle, quote = null}) {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const [rawText, setRawText] = useState('');
    // Los descartes salen del DETALLE, no de la prop `document`: el listado puede venir de un fetch
    // viejo (poll de 8s), y acá el usuario está por buscar un dato en este texto — es el peor
    // momento para que el aviso llegue tarde.
    const [gaps, setGaps] = useState([]);
    // Cuál de las apariciones de la cita está enfocada (índice sobre `ranges`).
    const [current, setCurrent] = useState(0);
    const textRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !document) return undefined;
        setLoading(true);
        setRawText('');
        setGaps([]);
        // Guarda de carrera. Hasta el 2026-09-13 cada superficie montaba su propio visor y había que
        // cerrar y abrir para cambiar de documento; con el visor ÚNICO de la página el usuario
        // clickea el nombre de dos hechos seguidos y la respuesta del primero puede llegar después
        // de la del segundo — y pintar la cita de B resaltada contra el texto de A, que es peor que
        // no resaltar nada (review C-cita-04).
        let vigente = true;
        ClientMemoryApi.getDocument(document.id, clientId)
            .then((data) => {
                if (!vigente) return;
                setLoading(false);
                if (data && data.document && typeof data.document.raw_text === 'string') {
                    setRawText(data.document.raw_text);
                    setGaps(documentGaps(data.document));
                } else {
                    toast.error(t('client_memory.toast.load_error'));
                    toggle();
                }
            })
            .catch(() => {
                if (!vigente) return;
                setLoading(false);
                toast.error(t('client_memory.toast.load_error'));
                toggle();
            });
        return () => { vigente = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, document ? document.id : null, clientId]);

    const ranges = useMemo(
        () => (quote && rawText ? locateQuote(quote, rawText) : []),
        [quote, rawText]);
    // Los segmentos llevan su número de aparición para pintar la enfocada distinto.
    const segments = useMemo(() => {
        let hitIndex = -1;
        return splitByRanges(rawText, ranges).map((segment) => {
            if (segment.hit) hitIndex += 1;
            return {...segment, hitIndex: segment.hit ? hitIndex : -1};
        });
    }, [rawText, ranges]);

    // Cada vez que cambia lo que se busca (otro documento, otra cita), se vuelve a la primera.
    useEffect(() => { setCurrent(0); }, [ranges]);

    const scrollToHit = useCallback((index) => {
        const container = textRef.current;
        if (!container) return;
        const mark = container.querySelectorAll('mark.cmem-raw-hit')[index];
        // jsdom no implementa `scrollIntoView`; en el navegador el contenedor scrolleable es el
        // `<pre>` y `block: 'center'` deja la cita a mitad de la ventana, con contexto arriba y abajo.
        if (mark && typeof mark.scrollIntoView === 'function') mark.scrollIntoView({block: 'center'});
    }, []);

    // Al terminar de cargar (o al cambiar de aparición) se lleva la vista hasta la cita.
    useEffect(() => {
        if (loading || !ranges.length) return;
        scrollToHit(current);
    }, [loading, ranges, current, scrollToHit]);

    const total = ranges.length;
    const goTo = (index) => {
        const next = (index + total) % total;
        setCurrent(next);
        // Si es la misma aparición (el usuario scrolleó y quiere volver), el efecto de arriba no
        // se dispara: se scrollea directo.
        if (next === current) scrollToHit(next);
    };

    /**
     * TRES estados, no dos (QA-VIS-cita-01).
     *
     * Hasta el 2026-09-13 el visor sólo distinguía «la encontré» de «no la encontré», y sobre un PDF
     * escaneado —cuyo crudo es el marcador `[PDF: nombre]`, 38 caracteres— afirmaba «La cita no
     * aparece textual en este material» y sugería buscar con Ctrl+F. Las dos cosas eran falsas: no
     * es que la cita esté parafraseada, es que **no hay texto donde buscarla**, y el Ctrl+F no lleva
     * a ningún lado. Es justo el consejo-que-no-sirve que este mismo frente evita en el vacío del
     * buscador de documentos.
     *
     * El backend ya modela los tres (`fact_grounding` devuelve `True` / `False` / `None`, con
     * `UNGROUNDABLE_PREFIXES` para el tercero); el visor los colapsaba en dos y afirmaba lo que el
     * backend se cuida de no afirmar.
     */
    const noText = textIsUngroundable(rawText);
    const quoteState = total > 0 ? 'found' : (noText ? 'no-text' : 'missing');

    // La barra entera cuelga de la CITA. Atarla además a `web_view_link` le agregaba un «Abrir el
    // original» a cualquier documento de Drive abierto desde «Ver contenido», que antes de este
    // frente no estaba ahí — y rompía la promesa de que sin cita el visor es el de siempre.
    const showToolbar = !loading && !!quote;

    return (
        // `scrollable` deja la cabecera fija y hace scrollear al cuerpo. Sin él, medido en una
        // notebook (`innerHeight` 667), el diálogo daba 723 px y arrancaba en `top: -22`: el TÍTULO
        // del documento —lo único que dice qué se está mirando— quedaba cortado arriba del borde de
        // la pantalla en el primer render. Lo desbordó este frente, que le sumó al modal la barra de
        // la cita encima de un banner que ya estaba y de un `<pre>` que se lleva 60vh
        // (QA-VIS-cita-02).
        <Modal isOpen={isOpen} toggle={toggle} className="cmem-raw-modal" scrollable>
            <ModalHeader toggle={toggle}>
                {document ? document.title : t('client_memory.documents.raw_title')}
            </ModalHeader>
            <ModalBody className="cmem-raw-modal__body">
                {loading ? (
                    <div className="box-loader"><Spinner color="positive"/></div>
                ) : (
                    <>
                        {/* Acá el aviso va ENTERO, no en un tooltip: es la pantalla donde alguien
                            busca un dato en el texto y no lo encuentra. Esconderlo detrás de un
                            hover sería dejarlo justo donde no se ve. */}
                        {gaps.length > 0 && (
                            <div className="cmem-raw-gap-banner" role="status">
                                <div className="cmem-raw-gap-title">
                                    <FaIcon iconName="triangle-exclamation"/>
                                    <span>{t('client_memory.content_gap.badge')}</span>
                                </div>
                                <ContentGapsExplanation gaps={gaps}/>
                            </div>
                        )}

                        {showToolbar && (
                            <div className="cmem-raw-toolbar">
                                {/* La cita se muestra como children, nunca interpolada en una
                                    clave: la escribió la extracción, no nosotros.
                                    ⚠️ El `role="status"` NO envuelve la caja entera: vive en el
                                    `<span>` del contador, abajo. Con la caja como live region, cada
                                    paso de ↑/↓ re-anunciaba la cita completa además del «2 de 5»
                                    (review C-cita-08). */}
                                <div className={`cmem-raw-quote -${quoteState}`}>
                                    <span className="cmem-raw-quote-label">
                                        {t('client_memory.documents.quote_label')}
                                    </span>
                                    <q className="cmem-raw-quote-text">{quote}</q>

                                    {quoteState === 'no-text' && (
                                        <span className="cmem-raw-quote-state">
                                            <FaIcon iconName="circle-info"/>
                                            <span>{t('client_memory.documents.quote_no_text')}</span>
                                            <span className="cmem-raw-quote-hint">
                                                {t('client_memory.documents.quote_no_text_hint')}
                                            </span>
                                        </span>
                                    )}

                                    {quoteState === 'missing' && (
                                        <span className="cmem-raw-quote-state">
                                            <FaIcon iconName="triangle-exclamation"/>
                                            <span>{t('client_memory.documents.quote_missing')}</span>
                                            {/* Dos causas legítimas, y el payload dice cuál es: si el
                                                documento declaró descartes, lo más probable es que la
                                                cita viva justo en lo que no dejó texto — mandar a
                                                buscarla con Ctrl+F ahí sería mandar a buscar algo que
                                                no está (review C-cita-09). */}
                                            <span className="cmem-raw-quote-hint">
                                                {t(gaps.length > 0
                                                    ? 'client_memory.documents.quote_missing_gaps_hint'
                                                    : 'client_memory.documents.quote_missing_hint')}
                                            </span>
                                        </span>
                                    )}

                                    {quoteState === 'found' && (
                                        <span className="cmem-raw-quote-nav">
                                            <FaIcon iconName="circle-check"/>
                                            <span role="status">
                                                {total === 1
                                                    ? t('client_memory.documents.quote_found')
                                                    : t('client_memory.documents.quote_found_many', {n: total})}
                                            </span>
                                            {total > 1 && (
                                                <>
                                                    <button type="button" className="cmem-raw-quote-step"
                                                            aria-label={t('client_memory.documents.quote_prev')}
                                                            onClick={() => goTo(current - 1)}>
                                                        <FaIcon iconName="chevron-up"/>
                                                    </button>
                                                    <span className="cmem-raw-quote-pos" role="status">
                                                        {t('client_memory.documents.quote_position',
                                                            {i: current + 1, n: total})}
                                                    </span>
                                                    <button type="button" className="cmem-raw-quote-step"
                                                            aria-label={t('client_memory.documents.quote_next')}
                                                            onClick={() => goTo(current + 1)}>
                                                        <FaIcon iconName="chevron-down"/>
                                                    </button>
                                                </>
                                            )}
                                            <button type="button" className="cmem-raw-quote-go"
                                                    onClick={() => goTo(current)}>
                                                {t('client_memory.documents.quote_go')}
                                            </button>
                                        </span>
                                    )}
                                </div>
                                {document && document.web_view_link && (
                                    <a className="cmem-raw-original" href={document.web_view_link}
                                       target="_blank" rel="noopener noreferrer">
                                        {t('client_memory.documents.open_original')}
                                        <FaIcon iconName="up-right-from-square"/>
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Una imagen o un PDF escaneado no tienen crudo que mostrar: el visor
                            vacío se lee como «se rompió». El aviso de arriba ya explica por qué. */}
                        {/* `tabIndex={0}` NO es decorativo: el `<pre>` es el contenedor con scroll
                            de un documento de hasta 200k caracteres, y sin foco un usuario de
                            teclado no puede scrollearlo (WCAG 2.1.1) — o sea que no puede leer
                            alrededor de la cita, que es justo el trabajo que esta pantalla habilita
                            (review C-cita-08). */}
                        {rawText.trim() || !gaps.length ? (
                            <pre className="cmem-raw-text" ref={textRef}
                                 tabIndex={0} role="region"
                                 aria-label={t('client_memory.documents.raw_title')}>
                                {segments.map((segment, index) => (segment.hit ? (
                                    <mark key={index}
                                          className={`cmem-raw-hit ${segment.hitIndex === current ? '-current' : ''}`}>
                                        {segment.text}
                                    </mark>
                                ) : (
                                    <React.Fragment key={index}>{segment.text}</React.Fragment>
                                )))}
                            </pre>
                        ) : (
                            <p className="cmem-raw-empty">{t('client_memory.documents.raw_empty_gaps')}</p>
                        )}
                    </>
                )}
            </ModalBody>
        </Modal>
    );
}
